import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { deskBoardMonthlyReport, deskBoardPause, deskBoardQuery, type DeskBoard } from '../api/desk'
import { errorMessages } from '../api/client'
import { handedReasonLabel } from './handedReasons'
import './deskScreens.css'

const WAITING_LABELS: Array<[keyof DeskBoard['waiting'], string]> = [
  ['person', 'On a person'],
  ['approval', 'On an approval'],
  ['vendor', 'On a vendor'],
  ['plan', 'On a plan'],
  ['order', 'On an order'],
]

function duration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  if (s < 3600) return `${Math.floor(s / 60)}m`
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`
}

function usd(value: number | null): string {
  return value === null ? '-' : `${value.toFixed(2)} USD`
}

function money(price: number | null, currency: string): string {
  return price === null ? '-' : `${price.toFixed(2)}${currency !== '' ? ` ${currency}` : ''}`
}

function companyFromUrl(): number | undefined {
  const id = Number(new URLSearchParams(window.location.search).get('company') ?? '')
  return Number.isInteger(id) && id > 0 ? id : undefined
}

function Tile({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="panel panel-default od-desk-board-tile" style={{ flex: '1 1 160px', marginBottom: 0 }}>
      <div className="panel-body">
        <div className="text-muted small">{label}</div>
        <div style={{ fontSize: 26, fontWeight: 600 }}>{value}</div>
        {hint && <div className="text-muted small">{hint}</div>}
      </div>
    </div>
  )
}

function Section({ title, count, children }: { title: string; count?: number; children: ReactNode }) {
  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <strong>{title}</strong>
        {count !== undefined && <span className="badge" style={{ marginLeft: 8 }}>{count}</span>}
      </div>
      {children}
    </div>
  )
}

export function DeskBoardPage() {
  const [companyId] = useState(companyFromUrl)
  const { data, isError, error, isPending } = useQuery({ ...deskBoardQuery(companyId), refetchInterval: 60000 })

  if (isPending) return <div className="container" style={{ paddingTop: 24 }}>Loading...</div>
  if (isError) {
    return (
      <div className="container" style={{ paddingTop: 24 }}>
        <p className="text-danger">{errorMessages(error).join(' ') || 'This page is not available for your account.'}</p>
      </div>
    )
  }
  const h = data.headline

  return (
    <div className="container od-desk od-desk-board" style={{ paddingTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ marginTop: 0, marginBottom: 4 }}>IT department - {data.company.name}</h2>
          <Heartbeat beat={data.heartbeat} />
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <MonthlyReport board={data} companyId={companyId} />
          <PauseControl board={data} companyId={companyId} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '16px 0' }}>
        <Tile label="Fixed on its own today" value={h.fixed_today} />
        <Tile label="Fixed on its own this month" value={h.fixed_month} hint="confirmed by the person, or held without complaint" />
        <Tile
          label="Resolved this month"
          value={`${h.rate}%`}
          hint={`${h.resolved_month} of ${h.taken_month} requests (${h.silent_month} with no answer from the person)`}
        />
        <Tile label="Cost per confirmed resolution" value={usd(h.cost_per_confirmed)} hint={`${usd(h.cost_usd)} this month`} />
        <Tile label="CSAT" value={h.csat === null ? '-' : h.csat.toFixed(2)} hint="out of 5" />
        <Tile label="Reopened" value={h.reopens} hint={`${h.handed_month} handed to a colleague`} />
      </div>

      <Section title="Waiting">
        <div className="panel-body" style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          {WAITING_LABELS.map(([key, label]) => (
            <div key={key}>
              <span className="text-muted">{label}:</span> <strong>{data.waiting[key]}</strong>
            </div>
          ))}
          <div className="text-muted">
            Queue:{' '}
            {Object.keys(data.queue).length === 0
              ? 'empty'
              : Object.entries(data.queue)
                  .map(([state, n]) => `${state.replace(/_/g, ' ')} ${n}`)
                  .join(', ')}
          </div>
        </div>
      </Section>

      <Section title="Handed over to a colleague" count={data.handed.length}>
        {data.handed.length === 0 ? (
          <div className="panel-body text-muted">Nothing handed over.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table table-condensed" style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Assignee</th>
                  <th>Waiting for</th>
                  <th>Why</th>
                </tr>
              </thead>
              <tbody>
                {data.handed.map((r) => (
                  <tr key={r.run_id}>
                    <td>
                      <a href={`/app/easeedesk/ticket/${r.ticket_id}`}>#{r.ticket_id}</a> {r.subject}
                    </td>
                    <td>{r.assignee !== '' ? r.assignee : <span className="text-muted">nobody yet</span>}</td>
                    <td>{duration(r.age_seconds)}</td>
                    <td>{handedReasonLabel(r.reason)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Orders to place" count={data.orders.length}>
        {data.orders.length === 0 ? (
          <div className="panel-body text-muted">Nothing to buy.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table table-condensed" style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>What</th>
                  <th>Waiting for</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.orders.map((r) => (
                  <tr key={r.run_id}>
                    <td>
                      <a href={`/app/easeedesk/ticket/${r.ticket_id}`}>#{r.ticket_id}</a> {r.subject}
                    </td>
                    <td>
                      {r.items.length === 0
                        ? '-'
                        : r.items.map((i, n) => (
                            <div key={n}>
                              {i.quantity} x {i.name} <span className="text-muted">{money(i.price, i.currency)}</span>
                            </div>
                          ))}
                    </td>
                    <td>{duration(r.age_seconds)}</td>
                    <td>{r.request_id > 0 && <a href={`/app/service_shop?prepared=${r.request_id}`}>Open the cart</a>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Plans not launched" count={data.plans.length}>
        {data.plans.length === 0 ? (
          <div className="panel-body text-muted">No plan waiting.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table table-condensed" style={{ marginBottom: 0 }}>
              <tbody>
                {data.plans.map((r) => (
                  <tr key={r.run_id}>
                    <td>
                      <a href={`/app/easeedesk/ticket/${r.ticket_id}#plan-tab`}>#{r.ticket_id}</a> {r.subject}
                    </td>
                    <td>{duration(r.age_seconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title={`By request type, since ${data.month_start}`}>
        {data.by_type.length === 0 ? (
          <div className="panel-body text-muted">No request this month.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table table-condensed" style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Requests</th>
                  <th>Confirmed</th>
                  <th>Resolved</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.by_type.map((t) => (
                  <tr key={t.request_type}>
                    <td>{t.request_type.replace(/_/g, ' ')}</td>
                    <td>{t.taken}</td>
                    <td>{t.confirmed}</td>
                    <td>{t.rate}%</td>
                    <td>{usd(t.cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  )
}

function Heartbeat({ beat }: { beat: DeskBoard['heartbeat'] }) {
  if (beat.at === null) return <span className="label label-danger">The desk has not run yet</span>
  return beat.stale ? (
    <span className="label label-danger">The desk has not run since {beat.at}</span>
  ) : (
    <span className="label label-success">The desk is running (last tick {beat.at})</span>
  )
}

function MonthlyReport({ board, companyId }: { board: DeskBoard; companyId?: number }) {
  const qc = useQueryClient()
  const [to, setTo] = useState('')
  const create = useMutation({
    mutationFn: () =>
      deskBoardMonthlyReport(
        to
          .split(/[,;\s]+/)
          .map((e) => e.trim())
          .filter((e) => e !== ''),
        companyId,
      ),
    onSuccess: () => {
      setTo('')
      void qc.invalidateQueries({ queryKey: ['desk', 'board'] })
    },
  })
  const report = board.report
  if (report.has_monthly && report.report_id !== null) {
    return (
      <a className="btn btn-default" href={`/app/reports/saved/${report.report_id}`}>
        Monthly report (sent on the 1st)
      </a>
    )
  }
  if (!report.can_create) return null

  return (
    <div style={{ maxWidth: 360 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <input
          className="form-control"
          style={{ flex: '1 1 180px', minWidth: 0 }}
          aria-label="Monthly report recipients"
          placeholder="Send to (empty = me)"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <button type="button" className="btn btn-default" disabled={create.isPending} onClick={() => create.mutate()}>
          Email me this every month
        </button>
      </div>
      {create.isError && <p className="text-danger">{errorMessages(create.error).join(' ') || 'The report was not created. Try again.'}</p>}
    </div>
  )
}

function PauseControl({ board, companyId }: { board: DeskBoard; companyId?: number }) {
  const qc = useQueryClient()
  const [reason, setReason] = useState('')
  const pause = board.pause
  const toggle = useMutation({
    mutationFn: (paused: boolean) => deskBoardPause(paused, reason.trim(), companyId),
    onSuccess: () => {
      setReason('')
      void qc.invalidateQueries({ queryKey: ['desk', 'board'] })
    },
  })
  if (pause.global) {
    return <span className="label label-warning">Paused everywhere by the platform</span>
  }
  if (pause.company && pause.inherited) {
    return <span className="label label-warning">Paused by your service provider</span>
  }
  if (!pause.can_pause) return null

  return (
    <div style={{ textAlign: 'right', maxWidth: 360 }}>
      {pause.company ? (
        <>
          <p className="text-warning" style={{ marginBottom: 6 }}>
            The AI desk is paused for this company.
          </p>
          <button type="button" className="btn btn-primary" disabled={toggle.isPending} onClick={() => toggle.mutate(false)}>
            Resume the desk
          </button>
        </>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 8 }}>
          <input
            className="form-control"
            style={{ flex: '1 1 160px', minWidth: 0 }}
            aria-label="Why pause"
            placeholder="Why (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <button type="button" className="btn btn-warning" disabled={toggle.isPending} onClick={() => toggle.mutate(true)}>
            Pause the desk
          </button>
        </div>
      )}
      {toggle.isError && <p className="text-danger">{errorMessages(toggle.error).join(' ') || 'The desk did not change. Try again.'}</p>}
    </div>
  )
}
